import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { deleteAlert } from "../../../components/alerts";
import DynamicTable from "../../../components/dynamicTable";
import AppHeader from "../../../components/header";
import useDebounce from "../../../components/hooks/useDebounce";
import AppLoader from "../../../components/loader";
import { AppContext } from "../../../lib/Context";
import {
  deleteContract,
  getAllContractById,
  updateContractStatuses,
} from "../../../services/ContractApi";
import { handleOpenSnackBar } from "../../../utils/helper";
import SearchTab from "./SearchTab";
import { useDispatch, useSelector } from "react-redux";
import { Stack } from "@mui/material";
import { resetLeadSearchFilter } from "../../../lib/redux/slices/searchKeysSlice";
import GlobalButton from "../../../components/button";
import AddIcon from "@mui/icons-material/Add";
import {
  resetPagination,
  setCurrentPage,
  setPageLimit,
  setTotalCount,
  setTotalPages,
} from "../../../lib/redux/slices/contractPaginateSlice";
import { setPhoneValidation } from "../../../lib/redux/slices/phoneValidationSlice";
import { setSignedDate } from "../../../lib/redux/slices/signedDateSlice";

const tableHeader = [
  "Prospect Details",
  "Connect Details",
  "Sale Value",
  "Docusign Status",
  "Sale Status",
  "Edit",
];

const displayRows = [
  "customer_details",
  "connectDetails",
  "paymentDetails",
  "contract_status",
  "sale_status",
  "actions",
];

const AllConnectionsPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [allContracts, setAllContracts] = useState([]);
  const { userData } = useContext(AppContext);
  const searchKeys = useSelector((state) => state.searchKeysReducer.searchKeys);
  const debounceSearchTerm = useDebounce(
    searchKeys?.leadSearchValue,
    1000,
    true
  );
  const { setSendType, handleResetData } = useContext(AppContext);
  const paginatedData = useSelector((state) => state.contractPaginatedReducer);

  useEffect(() => {
    if (userData) {
      fetchAllContracts(
        debounceSearchTerm,
        searchKeys?.leadSaleStatusValue,
        searchKeys?.leadStartDate,
        searchKeys?.leadEndDate,
        paginatedData.page,
        paginatedData.limit
      );
    }
  }, [
    userData,
    debounceSearchTerm,
    searchKeys?.leadSaleStatusValue,
    paginatedData.page,
    paginatedData.limit,
  ]);

  const fetchAllContracts = async (dt, ss, sd, ed, pageToLoad, limit) => {
    try {
      setIsLoading(true);
      const response = await getAllContractById(
        userData?._id,
        dt,
        ss,
        sd,
        ed,
        pageToLoad,
        limit
      );
      if (response?.data.status === "success") {
        let data = response?.data.data;
        let pagination = response?.data.pagination;
        setAllContracts(data);
        dispatch(setPageLimit(pagination.limit));
        dispatch(setTotalPages(pagination.totalPages));
        dispatch(setTotalCount(pagination.total));
        dispatch(setCurrentPage(pagination.page));
      } else {
        handleOpenSnackBar("error", response?.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteContract = (row) => {
    deleteAlert(true, row?._id, deleteContract, () =>
      fetchAllContracts(
        debounceSearchTerm,
        searchKeys?.leadSaleStatusValue,
        searchKeys?.leadStartDate,
        searchKeys?.leadEndDate,
        paginatedData.page,
        paginatedData.limit
      )
    );
  };

  const handleEditContract = (row) => {
    navigate("/edit-lead", { state: { data: row } });
    setSendType({
      type: "edit",
      id: row._id,
    });
  };

  const handleReset = () => {
    dispatch(resetLeadSearchFilter());
    dispatch(resetPagination());

    // fetchAllContracts("", [], "", "");
  };

  const handleUpdateSaleStatus = async (e, row) => {
    // Show SweetAlert confirmation prompt

    const payload = {
      saleStatus: e,
      log: {
        section: "Sale status update",
        field: "saleStatus",
        oldValue: row.saleStatus || "Empty",
        newValue: e || "Empty",
        message: `Sale status changed from ${row.saleStatus || "Empty"} to ${
          e || "Empty"
        }`,
        timestamp: Date.now(),
        changedBy: userData?.username,
      },
    };

    Swal.fire({
      title: "Are you sure?",
      text: "You want to update the status.",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, update it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setIsLoading(true);
          const response = await updateContractStatuses(row._id, payload);
          if (response?.data.status === "success") {
            fetchAllContracts(
              debounceSearchTerm,
              searchKeys?.leadSaleStatusValue,
              searchKeys?.leadStartDate,
              searchKeys?.leadEndDate,
              paginatedData.page,
              paginatedData.limit
            );
            // handleOpenSnackBar("success", "Sale status updated successfully");
            Swal.fire({
              title: "Updated!",
              text: "The sale status has been updated.",
              icon: "success",
            });
          } else {
            handleOpenSnackBar("error", response?.data.message);
          }
        } catch (error) {
          handleOpenSnackBar("error", error?.message);
        } finally {
          setIsLoading(false);
        }
      }
    });
  };

  const handleFilterByDate = () => {
    if (searchKeys?.leadStartDate && searchKeys?.leadEndDate) {
      dispatch(setCurrentPage(1));
      fetchAllContracts(
        debounceSearchTerm,
        searchKeys?.leadSaleStatusValue,
        searchKeys?.leadStartDate,
        searchKeys?.leadEndDate,
        1,
        paginatedData.limit
      );
    } else {
      handleOpenSnackBar("error", "Select date first");
    }
  };

  const handleAgentViewStatus = (row, value) => {

    Swal.fire({
      title: "Are you sure?",
      text: "You want to update the prospect status.",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, update it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setIsLoading(true);
          const response = await updateContractStatuses(row._id, {
            canAgentSee: value,
          });
          if (response?.data.status === "success") {
            fetchAllContracts(
              debounceSearchTerm,
              searchKeys?.leadSaleStatusValue,
              searchKeys?.leadStartDate,
              searchKeys?.leadEndDate,
              paginatedData.page,
              paginatedData.limit
            );

            Swal.fire({
              title: "Updated!",
              text: "The prospect status has been updated.",
              icon: "success",
            });
          } else {
            handleOpenSnackBar("error", response?.data.message);
          }
        } catch (error) {
          handleOpenSnackBar("error", error?.message);
        } finally {
          setIsLoading(false);
        }
      }
    });
  };

  const handleRowsPerPageChange = (event) => {
    const limit = parseInt(event.target.value, 10);
    dispatch(setPageLimit(limit));
    dispatch(setCurrentPage(1));
  };

  const handlePageChange = (event, newPage) => {
    const page = newPage + 1;
    dispatch(setCurrentPage(page));
  };

  const handleLinkedContract = (row) => {
    navigate("/edit-lead", { state: { data: row?.linkedContractDetails[0] } });
    setSendType({
      type: "edit",
      id: row?.linkedContractDetails[0]?._id,
    });
  };

  const renderTable = useMemo(() => {
    return (
      <DynamicTable
        tableData={allContracts}
        displayRows={displayRows}
        tableHeader={tableHeader}
        // showDelete
        showEdit
        handleDelete={handleDeleteContract}
        handleUpdateSaleStatus={handleUpdateSaleStatus}
        handleEdit={handleEditContract}
        handleAgentViewStatus={handleAgentViewStatus}
        isPaginated={true}
        paginatedData={paginatedData}
        onRowsPerPageChange={handleRowsPerPageChange}
        onPageChange={handlePageChange}
        handleLinkedContract={handleLinkedContract}
      />
    );
  }, [allContracts]);

  return (
    <AppHeader>
      <AppLoader isVisible={isLoading} />

      <Stack alignItems={"flex-end"}>
        <GlobalButton
          variant="custom_gradient"
          title="Add Lead"
          startIcon={<AddIcon />}
          handleClickBtn={() => {
            navigate("/add-lead");
            handleResetData(true);
            dispatch(setPhoneValidation(null));
            dispatch(setSignedDate(null));
          }}
          style={{
            width: { xs: "100%", sm: 130 },
            borderRadius: "100px",
          }}
        />
      </Stack>

      <SearchTab
        allContracts={allContracts}
        onFilterDate={handleFilterByDate}
        handleReset={handleReset}
      />
      {renderTable}
      {/* <DynamicTable
        tableData={allContracts}
        displayRows={displayRows}
        tableHeader={tableHeader}
        // showDelete
        showEdit
        handleDelete={handleDeleteContract}
        handleUpdateSaleStatus={handleUpdateSaleStatus}
        handleEdit={handleEditContract}
        handleAgentViewStatus={handleAgentViewStatus}
      /> */}
    </AppHeader>
  );
};

export default AllConnectionsPage;
